import type {
  Notification,
  NotificationPreference,
  NotificationSchedule,
  NotificationSendProblem,
  NotificationsRepository,
  Page,
  PushOverview,
  PushStatus,
  WriteResult,
} from "@estelle/domain";

import { apiFetch } from "@/adapters/api/base";

/** Phone and desktop push (#446, slice 5): the caller's devices, and the administrator's overview. */
const PUSH = "/api/push";

async function pushWrite<T>(
  path: string,
  body?: unknown,
): Promise<WriteResult<T>> {
  const response = await apiFetch(`${PUSH}${path}`, {
    method: "POST",
    headers:
      body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<T> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  throw new Error(`Push request failed: ${String(response.status)} on ${path}`);
}

async function pushRead<T>(path: string): Promise<T> {
  const response = await apiFetch(`${PUSH}${path}`);
  if (!response.ok) {
    throw new Error(
      `Push request failed: ${String(response.status)} on ${path}`,
    );
  }
  return (await response.json()) as T;
}

/**
 * The signed-in person's notifications, over HTTP (E6, #446).
 *
 * The API returns the domain shapes and scopes every call to the session's own
 * user, so there is nothing here that names a person: no id is ever sent that
 * could be changed to reach another's rows. Nothing is mapped.
 */

const BASE = "/api/notifications";

/** The administrator-only schedule: the summary time and the global quiet hours. */
const SCHEDULE = "/api/notification-schedule";

/**
 * A write's outcome. A refusal arrives as a `WriteResult` body with a 4xx
 * status — a rejection to show, not a failure to throw. Anything else is a
 * transport failure, which does throw.
 */
async function write<T>(
  path: string,
  method: "POST" | "PATCH",
  body?: unknown,
): Promise<WriteResult<T>> {
  const response = await apiFetch(`${BASE}${path}`, {
    method,
    headers:
      body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<T> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  throw new Error(
    `Notifications ${method} failed: ${String(response.status)} on ${path}`,
  );
}

export function createApiNotifications(): NotificationsRepository {
  return {
    list: async (query) => {
      const params = new URLSearchParams();
      if (query?.limit !== undefined) params.set("limit", String(query.limit));
      if (query?.cursor !== undefined) params.set("cursor", query.cursor);
      const suffix = params.toString();
      const response = await apiFetch(`${BASE}${suffix ? `?${suffix}` : ""}`);
      if (!response.ok) {
        throw new Error(
          `Notifications request failed: ${String(response.status)}`,
        );
      }
      return (await response.json()) as Page<Notification>;
    },

    unreadCount: async () => {
      const response = await apiFetch(`${BASE}/unread-count`);
      if (!response.ok) {
        throw new Error(
          `Notifications request failed: ${String(response.status)} on /unread-count`,
        );
      }
      const body = (await response.json()) as { count: number };
      return body.count;
    },

    markRead: (id) =>
      write<Notification>(`/${encodeURIComponent(id)}/read`, "POST"),

    markAllRead: () => write<{ updated: number }>("/read-all", "POST"),

    preferences: async () => {
      const response = await apiFetch(`${BASE}/preferences`);
      if (!response.ok) {
        throw new Error(
          `Notifications request failed: ${String(response.status)} on /preferences`,
        );
      }
      return (await response.json()) as NotificationPreference[];
    },

    updatePreferences: (changes) =>
      write<NotificationPreference[]>("/preferences", "PATCH", { changes }),

    getSchedule: async () => {
      const response = await apiFetch(SCHEDULE);
      if (!response.ok) {
        throw new Error(
          `Notification schedule request failed: ${String(response.status)}`,
        );
      }
      return (await response.json()) as NotificationSchedule;
    },

    saveSchedule: async (patch) => {
      const response = await apiFetch(SCHEDULE, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      });
      const result = (await response
        .json()
        .catch(() => null)) as WriteResult<NotificationSchedule> | null;
      if (result !== null && typeof result === "object" && "ok" in result) {
        return result;
      }
      throw new Error(
        `Notification schedule save failed: ${String(response.status)}`,
      );
    },

    listSendProblems: async () => {
      const response = await apiFetch(`${SCHEDULE}/problems`);
      if (!response.ok) {
        throw new Error(
          `Notification schedule request failed: ${String(response.status)} on /problems`,
        );
      }
      const body = (await response.json()) as {
        items: NotificationSendProblem[];
      };
      return body.items;
    },

    pushStatus: () => pushRead<PushStatus>("/status"),

    registerPushDevice: (input) => pushWrite("/devices", input),

    removePushDevice: (device) =>
      pushWrite<{ removed: number }>("/devices/remove", device),

    sendPushTest: () => pushWrite<{ sent: number }>("/test"),

    pushOverview: () => pushRead<PushOverview>("/overview"),
  };
}
